import { useState } from 'react';
import WidgetFrame from '../../components/widget-frame';
import type { WidgetEvent } from '../../lib/chat-api';
import { cn } from '@/lib/utils';

/* Routes a chat widget to its renderer: ask_mcq → clickable Mcq component, */
/* otherwise the sandboxed HTML frame (render_widget / render_interactive / */
/* cours_*). Extracted verbatim from Chat.tsx (decomposition, Phase 4). */
export function WidgetView({
  widget,
  locked,
  onResult: _onResult,
}: {
  widget: WidgetEvent;
  locked: boolean;
  onResult: (result: unknown) => void;
}) {
  if (widget.name === 'ask_mcq') {
    /* Active ask_mcq shows in ChatMcqModal. Only render locked version in bubble as history. */
    if (!locked) return null;
    return <Mcq widget={widget} locked={true} onAnswer={() => undefined} />;
  }
  if (widget.name === 'ask_form') {
    /* Active ask_form shows in ChatFormModal. History is the user's transcript bubble — nothing to render here. */
    return null;
  }
  return (
    <WidgetFrame
      html={widget.html ?? ''}
      title={widget.title}
      height={widget.height}
      bridgeToolCallId={widget.interactive && !locked ? widget.toolCallId : undefined}
    />
  );
}

function Mcq({
  widget,
  locked,
  onAnswer,
}: {
  widget: WidgetEvent;
  locked: boolean;
  onAnswer: (indices: number[]) => void;
}) {
  const args = (widget.args ?? {}) as {
    question?: string;
    options?: string[];
    allowMultiple?: boolean;
    title?: string;
  };
  const options = args.options ?? [];
  const multi = !!args.allowMultiple;
  const [sel, setSel] = useState<number[]>([]);

  const toggle = (i: number) => {
    if (locked) return;
    if (!multi) {
      onAnswer([i]); /* single choice answers immediately */
      return;
    }
    setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  };

  return (
    <div className="bg-card border border-border rounded-card p-[14px] shadow-card">
      {args.title && <div className="text-[11px] font-extrabold tracking-[0.5px] mb-[6px]">{args.title}</div>}
      <div className="font-bold mb-[10px]">{args.question}</div>
      <div className="flex flex-col gap-2">
        {options.map((opt, i) => {
          const on = sel.includes(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              disabled={locked}
              className={cn(
                "text-left p-[10px_12px] rounded-sm border border-border bg-card font-[inherit] text-[14px] cursor-pointer text-foreground disabled:cursor-default",
                on && "border-none bg-naia-blue text-card",
                locked && !on && "opacity-60",
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {multi && !locked && (
        <button
          type="button"
          onClick={() => sel.length && onAnswer(sel)}
          disabled={!sel.length}
          className="mt-[10px] p-[10px_16px] rounded-sm border-none bg-naia-blue text-card font-extrabold cursor-pointer font-[inherit] shadow-card disabled:bg-naia-info-bg disabled:text-muted-foreground disabled:cursor-not-allowed disabled:shadow-none"
        >
          Valider
        </button>
      )}
    </div>
  );
}
